import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useMessagesLive } from '../lib/messages';
import { lookup } from '@stitch/pbx-adapter/describe';
import type { Directory } from '@stitch/pbx-adapter';
import { api, type PersonPresence, type PresenceView } from '../api/client';
import { shownNumber } from '../lib/format';

/**
 * Who is on the phone, refreshed every few seconds while the page is open (and visible); for the
 * company's own people, changes the phone system's live connection hears show at once.
 */
export function usePresence(domain: string, every = 5000): PresenceView | undefined {
  const [view, setView] = useState<PresenceView>();
  const { state } = useAuth();
  const member = Boolean(state?.memberships?.some((m) => m.tenant === domain.toLowerCase()));
  useMessagesLive((e) => {
    if (e.type !== 'line' || e.tenant !== domain.toLowerCase() || !e.extension || !e.state) return;
    const { extension, state: line, dnd, since: at } = e;
    setView((v) => {
      const was = v?.people[extension];
      if (!v || !was) return v;
      // Who they're talking to comes with the next refresh (the event never says).
      const next: PersonPresence = { ...was, state: line, ...(dnd !== undefined ? { dnd } : {}), ...(at ? { since: at } : {}) };
      if (line === 'idle') {
        delete next.with;
        delete next.since;
        delete next.external;
      }
      return { ...v, people: { ...v.people, [extension]: next } };
    });
  }, member);
  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    const tick = async () => {
      try {
        const v = await api.presence(domain);
        if (live) setView(v);
      } catch {
        // Not available: the pages work without it.
      }
      if (live) timer = window.setTimeout(() => void tick(), document.hidden ? every * 6 : every);
    };
    void tick();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [domain, every]);
  return view;
}

function since(iso: string | undefined): string {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  return s < 3600 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** "On a call with Jordan Lee · 4:05", "Ringing", "Do not disturb", "Available", "No phone signed in". */
export function presenceText(p: PersonPresence, dir: Directory | undefined): { label: string; detail: string; tone: 'talk' | 'ring' | 'dnd' | 'ok' | 'off' } {
  const other = p.with ? p.with.name || (p.with.number ? lookup(dir, p.with.number).name : '') || shownNumber(p.with.number) : '';
  if (p.state === 'talking') return { label: other ? `On a call with ${other}` : 'On a call', detail: since(p.since), tone: 'talk' };
  if (p.state === 'ringing') return { label: other ? `Ringing: ${other}` : 'Ringing', detail: '', tone: 'ring' };
  if (p.dnd) return { label: 'Do not disturb', detail: '', tone: 'dnd' };
  if (p.phones > 0) return { label: 'Available', detail: '', tone: 'ok' };
  return { label: 'No phone signed in', detail: '', tone: 'off' };
}

export function PresenceBadge({ p, dir, compact }: { p: PersonPresence | undefined; dir: Directory | undefined; compact?: boolean }) {
  if (!p) return null;
  const t = presenceText(p, dir);
  return (
    <span className={`presence ${t.tone}`} title={t.label}>
      <span className="presence-dot" aria-hidden="true" />
      <span className={compact ? 'sr-only' : ''}>
        {t.label}
        {t.detail && <span className="muted mono"> · {t.detail}</span>}
      </span>
    </span>
  );
}
